<script setup lang="ts">
import { HybridlyFormData } from '@/@types/global';

import { ButtonColor } from '@/hooks/useButtonClasses';

import Button from '@/components/button/Button.vue';
import DangerDialog from '@/components/dialog/DangerDialog.vue';
import Fieldset from '@/components/fieldset/Fieldset.vue';
import Form from '@/components/form/Form.vue';
import FormFooter from '@/components/formFooter/FormFooter.vue';
import FormHeader from '@/components/formHeader/FormHeader.vue';
import FormTextInput from '@/components/formTextInput/FormTextInput.vue';

const props = defineProps<{
  form: HybridlyFormData<{
    type: string;
    display_name: string;
  }>;
  isEditing?: boolean;
  returnUrl?: string;
}>();

const isCancelDialogOpen = ref(false);

const { onBackForward } = useBackForward();

// TODO: Fix intecepting the back button
onBackForward(() => {
  router.abort();
});

function handleToggleCancelDialog() {
  if (props.form.isDirty) {
    isCancelDialogOpen.value = !isCancelDialogOpen.value;
    return;
  }
  handleRedirect();
}

function handleRedirect() {
  router.navigate({ url: route('ap-courses.index') });
}
</script>

<template>
  <Form @submit.prevent="form.submit">
    <template #header>
      <FormHeader
        canClose
        :title="`${isEditing ? 'Edit' : 'Add'} a AP Course`"
        :description="
          isEditing
            ? 'Use the form below to edit the AP course.'
            : 'Fill out the form below add a new AP course.'
        "
        @onClose="handleToggleCancelDialog"
      />
    </template>
    <Fieldset title="AP course information">
      <FormTextInput
        type="text"
        name="display_name"
        label="AP course name"
        :modelValue="form.fields.display_name"
        :error="form.errors.display_name"
        @update:modelValue="(value) => (form.fields.display_name = value as string)"
      />
    </Fieldset>
    <template #footer>
      <FormFooter>
        <Button
          :color="ButtonColor.primary"
          :isDisabled="form.processing || !form.isDirty"
          :isLoading="form.processing"
          type="submit"
          variant="solid"
          >Confirm</Button
        >
        <Button
          :color="ButtonColor.slate"
          type="button"
          variant="outlined"
          @click="handleToggleCancelDialog"
        >
          Back to AP courses
        </Button>
      </FormFooter>
    </template>
  </Form>
  <DangerDialog
    :title="`Cancel ${isEditing ? 'editing' : 'adding a new'} AP course`"
    :message="`Are you sure you want to cancel ${
      isEditing ? 'editing' : 'adding'
    } this AP course? All ${isEditing ? 'changes' : 'information'} will be lost.`"
    :isOpen="isCancelDialogOpen"
    cancelButtonLabel="Stay on this page"
    confirmButtonLabel="Go back to AP courses"
    @onCancel="handleToggleCancelDialog"
    @onClose="handleToggleCancelDialog"
    @onConfirm="handleRedirect"
  />
</template>
